import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Zap, Plus, Trash2, ArrowRight, Clock, CheckCircle2,
  Loader2, Play, Pause, History, ChevronRight, Settings2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import AdviserPicker from '@/components/shared/AdviserPicker';

const STAGES = [
  { id: 'new', name: 'New Inquiry' },
  { id: 'qualified', name: 'Qualified' },
  { id: 'proposal', name: 'Proposal Sent' },
  { id: 'negotiating', name: 'Negotiating' },
  { id: 'won', name: 'Won' },
  { id: 'lost', name: 'Lost' },
];

const TRIGGER_LABELS = {
  lead_created: 'Lead Created',
  lead_updated: 'Lead Updated',
  stage_changed: 'Stage Changed',
  daily_check: 'Daily Check',
  inactivity: 'Inactivity Alert',
};

const ACTION_LABELS = {
  apply_template: 'Apply Template',
  notify: 'Notify',
  create_task: 'Create Task',
  assign: 'Assign',
  flag: 'Flag Lead',
  set_priority: 'Set Priority',
};

// ============ RULE CARD ============
function RuleCard({ rule, onToggle, onDelete }) {
  const isStageRule = rule.trigger === 'stage_changed';
  const stageCondition = rule.conditions?.find(c => c.field === 'stage');
  const templateAction = rule.actions?.find(a => a.type === 'apply_template');

  return (
    <div
      className={cn(
        "rounded-xl border p-4 transition-all",
        rule.enabled ? "border-[#C6A88F] bg-white" : "border-[#E6E0D9] bg-[#FDFCFB] opacity-70"
      )}
      data-testid={`automation-rule-${rule.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
            rule.enabled ? "bg-amber-50 border border-amber-200" : "bg-gray-50 border border-gray-200"
          )}>
            <Zap className={cn("w-4 h-4", rule.enabled ? "text-amber-600" : "text-gray-400")} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-[#2C2420] truncate">{rule.name}</h4>
              {rule.isDefault && <Badge variant="outline" className="text-[10px] shrink-0">Default</Badge>}
            </div>
            <p className="text-xs text-[#5D524D] mt-0.5 line-clamp-1">{rule.description}</p>

            {/* Visual rule summary */}
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <Badge className="text-[10px] bg-blue-50 text-blue-700">{TRIGGER_LABELS[rule.trigger] || rule.trigger}</Badge>
              {stageCondition && (
                <>
                  <ArrowRight className="w-3 h-3 text-[#8C8078]" />
                  <Badge className="text-[10px] bg-purple-50 text-purple-700">
                    Stage = {STAGES.find(s => s.id === stageCondition.value)?.name || stageCondition.value}
                  </Badge>
                </>
              )}
              {rule.actions?.map((a, i) => (
                <span key={i} className="flex items-center gap-1">
                  <ArrowRight className="w-3 h-3 text-[#8C8078]" />
                  <Badge className="text-[10px] bg-emerald-50 text-emerald-700">
                    {ACTION_LABELS[a.type] || a.type}
                    {a.templateId && `: ${a.templateName || a.templateId}`}
                  </Badge>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Switch
            checked={rule.enabled}
            onCheckedChange={() => onToggle(rule)}
            data-testid={`rule-toggle-${rule.id}`}
          />
          {!rule.isDefault && (
            <button
              onClick={() => onDelete(rule)}
              className="p-1.5 rounded-md hover:bg-red-50 text-[#8C8078] hover:text-red-600 transition-colors"
              data-testid={`rule-delete-${rule.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ CREATE RULE MODAL ============
function CreateRuleModal({ open, onClose, templates }) {
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerStage, setTriggerStage] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setTriggerStage('');
      setTemplateId('');
      setAssignTo('');
    }
  }, [open]);

  // Auto-generate name
  useEffect(() => {
    if (triggerStage && templateId) {
      const stageName = STAGES.find(s => s.id === triggerStage)?.name || triggerStage;
      const tplName = templates.find(t => t.id === templateId)?.name || templateId;
      setName(`On ${stageName} → ${tplName}`);
      setDescription(`When a lead moves to ${stageName}, automatically apply the ${tplName} workflow template.`);
    }
  }, [triggerStage, templateId, templates]);

  const handleSave = async () => {
    if (!triggerStage || !templateId) {
      toast.error('Please select a stage and template');
      return;
    }
    setSaving(true);
    try {
      const tplName = templates.find(t => t.id === templateId)?.name || '';
      await api.post(token, '/leads/automation/rules', {
        name: name.trim() || `Stage rule → ${templateId}`,
        description: description.trim(),
        trigger: 'stage_changed',
        conditions: [{ field: 'stage', operator: 'equals', value: triggerStage }],
        actions: [{
          type: 'apply_template',
          templateId,
          templateName: tplName,
          assignTo: assignTo || null,
        }],
        enabled: true,
      });
      toast.success('Automation rule created');
      onClose(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="create-rule-modal">
        <DialogHeader>
          <DialogTitle>Create Automation Rule</DialogTitle>
          <DialogDescription>
            When a lead changes stage, automatically apply a workflow template.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Visual Pipeline */}
          <div className="bg-[#F5F3F0] rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-[#2C2420]">
              <Zap className="w-4 h-4 text-amber-600" />
              When lead moves to...
            </div>
            <Select value={triggerStage} onValueChange={setTriggerStage}>
              <SelectTrigger data-testid="rule-stage-select">
                <SelectValue placeholder="Select pipeline stage..." />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 text-sm font-medium text-[#2C2420]">
              <ArrowRight className="w-4 h-4 text-emerald-600" />
              Then apply template...
            </div>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="rule-template-select">
                <SelectValue placeholder="Select workflow template..." />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} ({t.taskCount} tasks)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div>
              <Label className="text-xs text-[#5D524D]">Assign tasks to (optional)</Label>
              <div className="mt-1">
                <AdviserPicker
                  value={assignTo}
                  onChange={(v) => setAssignTo(v.trim())}
                  placeholder="Choose internal adviser"
                  testId="rule-assign-to"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          {triggerStage && templateId && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-xs text-emerald-700 font-medium">Rule Preview</p>
              <p className="text-sm text-emerald-800 mt-1">{description || name}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="rule-cancel">Cancel</Button>
          <Button
            className="caffeine-btn"
            onClick={handleSave}
            disabled={!triggerStage || !templateId || saving}
            data-testid="rule-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            {saving ? 'Creating...' : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ ACTIVITY LOG MODAL ============
function ActivityLogModal({ open, onClose }) {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get(token, '/leads/automation/logs')
        .then(res => setLogs(res.data.logs || []))
        .catch(() => toast.error('Failed to load logs'))
        .finally(() => setLoading(false));
    }
  }, [open, token]);

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="automation-log-modal">
        <DialogHeader>
          <DialogTitle>Automation Activity Log</DialogTitle>
          <DialogDescription>History of triggered automation rules</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8">
            <History className="w-10 h-10 text-[#D4CCC6] mx-auto mb-2" />
            <p className="text-sm text-[#5D524D]">No automation activity yet</p>
            <p className="text-xs text-[#8C8078] mt-1">Rules will fire when leads change pipeline stage</p>
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map(log => (
              <div key={log.id} className="rounded-lg border border-[#E6E0D9] p-3" data-testid={`log-entry-${log.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-[#2C2420]">
                    Lead: {log.context?.leadName || log.leadId}
                  </span>
                  <span className="text-[10px] text-[#8C8078]">
                    {log.createdAt ? format(parseISO(log.createdAt), 'MMM d, h:mm a') : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#5D524D] mb-2">
                  <Badge className="text-[10px] bg-blue-50 text-blue-700">{log.trigger}</Badge>
                  {log.context?.oldStage && (
                    <>
                      <span>{STAGES.find(s => s.id === log.context.oldStage)?.name || log.context.oldStage}</span>
                      <ArrowRight className="w-3 h-3" />
                      <span className="font-medium">{STAGES.find(s => s.id === log.context.stage)?.name || log.context.stage}</span>
                    </>
                  )}
                </div>
                {log.executedRules?.map((exec, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-emerald-50 rounded px-2 py-1 mt-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span className="text-emerald-700">
                      {exec.ruleName}: {exec.result?.templateName ? `Applied "${exec.result.templateName}" (${exec.result.tasksCreated} tasks)` : exec.result?.status}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN COMPONENT ============
export default function AutomationRules() {
  const { token } = useAuth();
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [rulesRes, tplRes] = await Promise.all([
        api.get(token, '/leads/automation/rules'),
        api.get(token, '/task-templates'),
      ]);
      setRules(rulesRes.data.rules || []);
      const allTpls = [...(tplRes.data.system || []), ...(tplRes.data.custom || [])];
      setTemplates(allTpls);
    } catch {
      toast.error('Failed to load automation data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = async (rule) => {
    try {
      await api.put(token, `/leads/automation/rules/${rule.id}/toggle`);
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r));
      toast.success(rule.enabled ? 'Rule disabled' : 'Rule enabled');
    } catch {
      toast.error('Failed to toggle rule');
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await api.delete(token, `/leads/automation/rules/${rule.id}`);
      setRules(prev => prev.filter(r => r.id !== rule.id));
      toast.success('Rule deleted');
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  const enabledCount = rules.filter(r => r.enabled).length;

  return (
    <div className="space-y-6" data-testid="automation-rules-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-heading font-semibold text-[#2C2420]">Automation Rules</h2>
            <Badge className="bg-amber-50 text-amber-700 text-xs">{enabledCount} active</Badge>
          </div>
          <p className="text-sm text-[#5D524D]">Auto-trigger workflows when leads change pipeline stage</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowLogs(true)} data-testid="show-automation-logs">
            <History className="w-4 h-4 mr-2" />Activity Log
          </Button>
          <Button className="caffeine-btn" onClick={() => setShowCreate(true)} data-testid="create-automation-rule">
            <Plus className="w-4 h-4 mr-2" />New Rule
          </Button>
        </div>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#E6E0D9] rounded-xl">
          <Zap className="w-12 h-12 text-[#D4CCC6] mx-auto mb-3" />
          <p className="text-[#5D524D] font-medium">No automation rules yet</p>
          <p className="text-sm text-[#8C8078] mt-1">Create your first rule to auto-apply templates on stage changes</p>
          <Button className="mt-4 caffeine-btn" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" />Create First Rule
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <CreateRuleModal
        open={showCreate}
        onClose={(created) => { setShowCreate(false); if (created) fetchData(); }}
        templates={templates}
      />
      <ActivityLogModal open={showLogs} onClose={() => setShowLogs(false)} />
    </div>
  );
}
